import { Activity } from "@/lib/models";
import Image from "next/image";
import ArrowRight from "@/components/icons/ArrowRight";
import { handleInternalNavigationButtonClick } from "@/lib/analytics/navigation";
import colors from "@/lib/static/colors";
import { HtmlRenderer } from "./HtmlRenderer";
import TriangleRight from "./icons/RightVector";
import HexagonsBg from "./icons/HexagonsBg";
import StarBg from "./icons/StarBg";
import Link from "next/link";

type FeaturedActivitySlideProps = {
  activity: Activity;
  index?: number;
};

const FeaturedActivitySlide = ({ activity }: FeaturedActivitySlideProps) => {
  const {
    competencyName,
    subCompetencyName,
    learnerOutcomeId,
    urlSlug,
    overview,
    name,
  } = activity;

  const learnerOutcomeColorHex = colors[`${learnerOutcomeId}-30`];
  const learnerOutcomeFillColorHex = colors[`${learnerOutcomeId}-50`];

  return (
    <Link
      href={`/activities/${urlSlug}`}
      onClick={() =>
        handleInternalNavigationButtonClick({ buttonName: "Read_more" })
      }
      aria-label={`${activity.name} Read more`}
      className={`relative flex-[0_0_100%] overflow-hidden rounded-3xl border bg-${learnerOutcomeId}-40 mt-4 small:w-[736px] medium:w-[976px] large:w-[1392px]`}
    >
      <HexagonsBg
        fill={learnerOutcomeFillColorHex}
        className="absolute bottom-0 left-0 shrink-0"
      />

      <div className="mx-6 grid h-full gap-4 small:grid-cols-2 medium:grid-cols-12 large:mx-0">
        <div className="relative z-10 small:col-span-1 small:col-start-1 small:ml-10 medium:col-span-6 large:ml-[60px]">
          <StarBg
            fill={learnerOutcomeFillColorHex}
            className="absolute left-[40%] top-[5%] h-6 w-6 shrink-0"
          />
          <div>
            <div className="mb-6 mt-6 font-teodor text-[36px] leading-none small:mb-8 small:mt-12 small:text-5xl medium:mt-14 medium:text-5xl">
              {name}
            </div>

            <div className="mb-4 font-roobert text-base leading-tight small:mb-6 small:text-xl large:mb-8">
              <HtmlRenderer html={overview} />
            </div>
            <div className="mb-4 hidden flex-col medium:flex medium:flex-row large:mb-6">
              <div className="flex-col small:mb-6 medium:mb-0">
                <p className="text-sm medium:pr-3">Competency:</p>
                <p className="text-sm font-semibold medium:pr-3">
                  {competencyName}
                </p>
              </div>

              <div className="ml-3 hidden pt-[11px] medium:block">
                <TriangleRight />
              </div>

              <div className="flex-col medium:pl-3">
                <p className="text-sm">Component Skill:</p>
                <p className="text-sm font-bold">{subCompetencyName}</p>
              </div>
            </div>
          </div>
          <div className="hidden h-12 items-center justify-center gap-1 rounded-full bg-black py-3.5 pl-4 pr-3 font-roobert text-base font-semibold disabled:opacity-20 medium:inline-flex">
            <span className={`text-[16px] text-${learnerOutcomeId}-30`}>
              Read more
            </span>
            <ArrowRight fill={`${learnerOutcomeColorHex}`} />
          </div>
          <StarBg
            fill={learnerOutcomeFillColorHex}
            className="absolute bottom-4 right-0 h-8 w-8 shrink-0"
          />
        </div>

        <div className="relative hidden small:block small:w-[50vw]">
          {activity.featuredImage1?.sourceUrl && (
            <Image
              src={activity?.featuredImage1?.sourceUrl}
              alt="ActivityImage1"
              width={330}
              height={186}
              className="h-full w-full object-cover object-left"
            />
          )}
        </div>
      </div>
    </Link>
  );
};

export default FeaturedActivitySlide;
